/**
 * Talk-o 聊天伙伴页面 - WebSocket 实时流式版本
 *
 * 【本次升级】
 * 1. 简洁专业、高级感并重的主页设计：柔和渐变、玻璃拟态、圆润动效
 * 2. 录音时展示实时音频波纹（Canvas + Web Audio Analyser）
 * 3. AI 回复时实时显示转写文本气泡
 * 4. AI 语音播放结束后自动回到"点击说话"待命状态
 *
 * 【架构】
 * - 前端录音 → WebSocket 二进制 → 后端 Gemini Live API
 * - 后端 PCM chunks → WebSocket 二进制 → 流式播放器
 * - 后端 AI 转写文本 → JSON ai_text → 前端文本气泡
 */

import { useState, useRef, useCallback, useEffect } from 'react'

// =============================================================================
// 类型定义
// =============================================================================

type AppState = 'init' | 'idle' | 'recording' | 'thinking' | 'speaking' | 'error'

type LogLevel = 'info' | 'error' | 'warn'
interface LogEntry {
  id: number
  level: LogLevel
  message: string
  timestamp: string
}

interface TurnCompleteMessage {
  type: 'turn_complete'
  stats?: {
    chunks: number
    total_bytes: number
    duration_seconds: number
  }
}

interface AiTextMessage {
  type: 'ai_text'
  text: string
  delta: string
}

interface ErrorMessage {
  type: 'error'
  message: string
}

type WebSocketMessage = TurnCompleteMessage | AiTextMessage | ErrorMessage

interface Persona {
  id: number
  name: string
  system_prompt: string
  is_active: boolean
  voice: string
  created_at: string | null
}

// =============================================================================
// 常量配置
// =============================================================================

const RECORDING_CONFIG = {
  MIN_DURATION_MS: 500,
  MAX_DURATION_MS: 30000,
  MIME_TYPES: [
    'audio/mp4',
    'audio/webm',
    'audio/webm;codecs=opus',
  ],
}

const AUDIO_CONFIG = {
  SAMPLE_RATE: 24000,
  BITS_PER_SAMPLE: 16,
  CHANNELS: 1,
}

// =============================================================================
// 辅助函数
// =============================================================================

const getWebSocketUrl = (personaId?: number): string => {
  /**
   * 获取 WebSocket 连接 URL
   * 自动适配本地直连与 ngrok 代理模式，并携带当前选中的 persona_id
   */
  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  const query = personaId ? `?persona_id=${personaId}` : ''

  // 1. 如果是通过 ngrok 访问 (HTTPS 环境)
  if (!isLocalhost && window.location.protocol === 'https:') {
    return `wss://${hostname}/ws/chat${query}`
  }

  // 2. 如果是本地开发环境 (直接连接后端 8080)
  if (isLocalhost) {
    return `ws://${hostname}:8080/ws/chat${query}`
  }

  // 3. 其他情况 (回退逻辑)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${hostname}${window.location.port ? ':' + window.location.port : ''}/ws/chat${query}`
}

const getSupportedMimeType = (): string | null => {
  if (!window.MediaRecorder) return null
  for (const type of RECORDING_CONFIG.MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      console.log(`[Talk-o] 使用音频格式: ${type}`)
      return type
    }
  }
  console.warn('[Talk-o] 未找到特定 MIME 类型，使用浏览器默认')
  return ''
}

// =============================================================================
// 【核心】流式 PCM 播放器（增强播放完成检测）
// =============================================================================

class StreamingPCMAudioPlayer {
  private audioContext: AudioContext | null = null
  private nextPlayTime: number = 0
  private sampleRate: number
  private isPlaying: boolean = false
  private turnComplete: boolean = false
  private scheduledCount: number = 0
  private endedCount: number = 0
  private onPlaybackComplete: (() => void) | null = null

  constructor(sampleRate: number = 24000) {
    this.sampleRate = sampleRate
  }

  async unlock(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: this.sampleRate,
      })
      console.log(`[AudioPlayer] AudioContext 创建成功，采样率: ${this.sampleRate}Hz`)
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
      console.log('[AudioPlayer] AudioContext 已恢复')
    }
  }

  startSession(onComplete?: () => void): void {
    if (!this.audioContext) {
      console.error('[AudioPlayer] 错误: 未解锁 AudioContext')
      return
    }

    this.nextPlayTime = this.audioContext.currentTime + 0.05
    this.isPlaying = true
    this.turnComplete = false
    this.scheduledCount = 0
    this.endedCount = 0
    this.onPlaybackComplete = onComplete || null

    console.log(`[AudioPlayer] 播放会话开始，起始时间: ${this.nextPlayTime.toFixed(3)}s`)
  }

  async playChunk(pcmData: ArrayBuffer): Promise<void> {
    if (!this.audioContext || !this.isPlaying) {
      console.warn('[AudioPlayer] 警告: 未准备好播放')
      return
    }

    try {
      const int16Data = new Int16Array(pcmData)
      const audioBuffer = this.audioContext.createBuffer(
        AUDIO_CONFIG.CHANNELS,
        int16Data.length,
        this.sampleRate,
      )
      const channelData = audioBuffer.getChannelData(0)
      for (let i = 0; i < int16Data.length; i++) {
        channelData[i] = int16Data[i] / 32768.0
      }

      const source = this.audioContext.createBufferSource()
      source.buffer = audioBuffer
      source.connect(this.audioContext.destination)

      const startTime = this.nextPlayTime
      source.start(startTime)
      this.nextPlayTime = startTime + audioBuffer.duration

      this.scheduledCount++
      source.onended = () => {
        this.endedCount++
        this.checkComplete()
      }

      console.log(
        `[AudioPlayer] 排队 chunk: ${pcmData.byteLength} bytes, ` +
        `播放时刻: ${startTime.toFixed(3)}s, ` +
        `时长: ${audioBuffer.duration.toFixed(3)}s`,
      )
    } catch (error) {
      console.error('[AudioPlayer] 播放 chunk 失败:', error)
    }
  }

  /**
   * 标记后端音频流已全部发送完毕
   * 播放器会等待已排队的音频全部播完后再触发完成回调
   */
  markTurnComplete(): void {
    this.turnComplete = true
    this.checkComplete()
  }

  private checkComplete(): void {
    if (!this.isPlaying) return
    if (this.turnComplete && this.endedCount >= this.scheduledCount) {
      console.log('[AudioPlayer] 全部音频播放完成')
      this.isPlaying = false
      if (this.onPlaybackComplete) {
        this.onPlaybackComplete()
      }
    }
  }

  stop(): void {
    this.isPlaying = false
    this.turnComplete = false
  }

  isActive(): boolean {
    return this.isPlaying
  }
}

// =============================================================================
// 【核心】WebSocket 管理器
// =============================================================================

class WebSocketManager {
  private ws: WebSocket | null = null
  private url: string
  private onMessage: (data: ArrayBuffer | WebSocketMessage) => void
  private onError: (error: string) => void
  private onClose: () => void

  constructor(
    url: string,
    onMessage: (data: ArrayBuffer | WebSocketMessage) => void,
    onError: (error: string) => void,
    onClose: () => void,
  ) {
    this.url = url
    this.onMessage = onMessage
    this.onError = onError
    this.onClose = onClose
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        console.log(`[WebSocket] 连接到: ${this.url}`)
        this.ws = new WebSocket(this.url)
        this.ws.binaryType = 'arraybuffer'

        this.ws.onopen = () => {
          console.log('[WebSocket] 连接已建立')
          resolve()
        }

        this.ws.onmessage = (event) => {
          if (event.data instanceof ArrayBuffer) {
            this.onMessage(event.data)
          } else {
            try {
              const message = JSON.parse(event.data) as WebSocketMessage
              this.onMessage(message)
            } catch (e) {
              console.error('[WebSocket] JSON 解析失败:', event.data)
            }
          }
        }

        this.ws.onerror = (error) => {
          console.error('[WebSocket] 连接错误:', error)
          this.onError('WebSocket 连接错误')
          reject(error)
        }

        this.ws.onclose = () => {
          console.log('[WebSocket] 连接已关闭')
          this.ws = null
          this.onClose()
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  sendAudio(audioBlob: Blob): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] 错误: 连接未建立')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        this.ws?.send(reader.result)
        console.log(`[WebSocket] 发送音频: ${reader.result.byteLength} bytes`)
      }
    }
    reader.readAsArrayBuffer(audioBlob)
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }
}

// =============================================================================
// 录音波纹可视化组件
// =============================================================================

interface RecordingWaveformProps {
  stream: MediaStream | null
  isRecording: boolean
}

const BAR_COUNT = 28

function RecordingWaveform({ stream, isRecording }: RecordingWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number>(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)

  useEffect(() => {
    if (!stream || !isRecording || !canvasRef.current) return

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
    const audioContext = new AudioContextClass()
    audioContextRef.current = audioContext

    const source = audioContext.createMediaStreamSource(stream)
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 128
    analyser.smoothingTimeConstant = 0.82
    source.connect(analyser)
    analyserRef.current = analyser

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    // 高 DPI 适配
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw)
      analyser.getByteFrequencyData(dataArray)

      const width = rect.width
      const height = rect.height
      ctx.clearRect(0, 0, width, height)

      const totalGap = width * 0.25
      const barWidth = (width - totalGap) / BAR_COUNT
      const gap = totalGap / (BAR_COUNT - 1)
      const centerX = width / 2

      for (let i = 0; i < BAR_COUNT; i++) {
        // 从中间向两侧映射频率，中间低频、两侧高频，视觉更对称
        const t = i / (BAR_COUNT - 1)
        const freqIndex = Math.floor(t * (bufferLength - 1))
        const value = dataArray[freqIndex] || 0
        const normalized = value / 255

        // 给中间柱子更高权重，形成"山峰"感
        const centerWeight = 1 - Math.abs(i - BAR_COUNT / 2) / (BAR_COUNT / 2)
        const barHeight = Math.max(6, normalized * height * 0.85 * (0.5 + centerWeight * 0.5))

        const x = centerX + (i - BAR_COUNT / 2) * (barWidth + gap) - barWidth / 2
        const y = (height - barHeight) / 2

        const gradient = ctx.createLinearGradient(0, y + barHeight, 0, y)
        gradient.addColorStop(0, '#7dd3fc') // sky-300
        gradient.addColorStop(0.6, '#818cf8') // indigo-300
        gradient.addColorStop(1, '#22d3ee') // cyan-300

        ctx.fillStyle = gradient
        ctx.shadowColor = 'rgba(56, 189, 248, 0.35)'
        ctx.shadowBlur = 10
        ctx.shadowOffsetY = 4

        if (ctx.roundRect) {
          ctx.beginPath()
          ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2)
          ctx.fill()
        } else {
          ctx.beginPath()
          ctx.arc(x + barWidth / 2, y + barHeight / 2, barWidth / 2, 0, Math.PI * 2)
          ctx.fill()
        }

        ctx.shadowBlur = 0
        ctx.shadowOffsetY = 0
      }
    }

    draw()

    return () => {
      cancelAnimationFrame(animationRef.current)
      try {
        source.disconnect()
        analyser.disconnect()
        audioContext.close()
      } catch (e) {
        // ignore
      }
      audioContextRef.current = null
      analyserRef.current = null
    }
  }, [stream, isRecording])

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-28 sm:h-32 transition-opacity duration-300"
      aria-label="录音声波"
    />
  )
}

// =============================================================================
// 图标组件（内联 SVG，避免额外依赖）
// =============================================================================

const MicIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
  </svg>
)

const StopIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
)

const SparkleIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2L14.09 8.26L21 9.27L16.5 14.14L17.59 21.02L12 17.77L6.41 21.02L7.5 14.14L3 9.27L9.91 8.26L12 2Z" />
  </svg>
)

const ThinkingIcon = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="12" r="10" opacity="0.2" />
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity="0.4" />
    <path d="M12 6v6l4 2" />
  </svg>
)

// =============================================================================
// 主应用组件
// =============================================================================

export default function TalkoChat() {

  const [appState, setAppState] = useState<AppState>('init')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // AI 转写文本（实时显示在气泡中）
  const [aiText, setAiText] = useState('')

  // AI 角色选择
  const [personas, setPersonas] = useState<Persona[]>([])
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null)
  const [loadingPersonas, setLoadingPersonas] = useState(false)
  const [showPersonaSelector, setShowPersonaSelector] = useState(false)

  // 调试日志
  const [debugLogs, setDebugLogs] = useState<LogEntry[]>([])
  const [showDebugPanel, setShowDebugPanel] = useState(false)
  const logIdRef = useRef(0)

  // 录音相关 refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingStreamRef = useRef<MediaStream | null>(null)
  const recordingStartTimeRef = useRef<number>(0)
  const audioChunksRef = useRef<Blob[]>([])
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 核心组件 refs
  const audioPlayerRef = useRef<StreamingPCMAudioPlayer | null>(null)
  const wsManagerRef = useRef<WebSocketManager | null>(null)

  // ---------------------------------------------------------------------------
  // 调试日志
  // ---------------------------------------------------------------------------

  const addLog = useCallback((message: string, level: LogLevel = 'info') => {
    const newLog: LogEntry = {
      id: logIdRef.current++,
      level,
      message: message.substring(0, 200),
      timestamp: new Date().toLocaleTimeString('zh-CN', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    }
    setDebugLogs((prev) => [...prev.slice(-49), newLog])

    if (level === 'error') {
      console.error(`[Talk-o-UI] ${message}`)
    } else {
      console.log(`[Talk-o-UI] ${message}`)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // 初始化与清理
  // ---------------------------------------------------------------------------

  useEffect(() => {
    audioPlayerRef.current = new StreamingPCMAudioPlayer(AUDIO_CONFIG.SAMPLE_RATE)

    return () => {
      wsManagerRef.current?.disconnect()
      audioPlayerRef.current = null
    }
  }, [])

  // ---------------------------------------------------------------------------
  // 加载 AI 角色列表
  // ---------------------------------------------------------------------------

  const fetchPersonas = useCallback(async () => {
    setLoadingPersonas(true)
    try {
      const response = await fetch('/api/personas')
      if (!response.ok) throw new Error('加载角色列表失败')
      const data = (await response.json()) as Persona[]
      setPersonas(data)
      // 默认选中当前激活角色，若无则选第一个
      if (data.length > 0 && !selectedPersona) {
        const active = data.find((p) => p.is_active) || data[0]
        setSelectedPersona(active)
        addLog(`当前角色: ${active.name}`)
      }
    } catch (error: any) {
      addLog(`加载角色失败: ${error.message}`, 'error')
    } finally {
      setLoadingPersonas(false)
    }
  }, [addLog, selectedPersona])

  useEffect(() => {
    fetchPersonas()
  }, [fetchPersonas])

  // ---------------------------------------------------------------------------
  // WebSocket 消息处理
  // ---------------------------------------------------------------------------

  const onPlaybackComplete = useCallback(() => {
    addLog('音频播放完成，回到待命状态')
    setIsPlaying(false)
    setAppState('idle')
    wsManagerRef.current?.disconnect()
  }, [addLog])

  const handleWebSocketMessage = useCallback(
    (data: ArrayBuffer | WebSocketMessage) => {
      // 二进制 PCM 音频数据
      if (data instanceof ArrayBuffer) {
        if (audioPlayerRef.current && !audioPlayerRef.current.isActive()) {
          addLog('收到第一块音频，启动流式播放器')
          setAppState('speaking')
          setIsPlaying(true)
          audioPlayerRef.current.startSession(onPlaybackComplete)
        }
        audioPlayerRef.current?.playChunk(data)
        return
      }

      // JSON 控制消息
      if (data.type === 'turn_complete') {
        addLog('AI 说话完毕，等待音频队列播放结束...')
        audioPlayerRef.current?.markTurnComplete()
      } else if (data.type === 'ai_text') {
        // 🌟 实时显示 AI 转写文本
        setAiText(data.text)
        addLog(`AI 文本: ${data.text.substring(0, 60)}`)
      } else if (data.type === 'error') {
        addLog(`后端错误: ${data.message}`, 'error')
        setErrorMsg(data.message)
        setAppState('error')
      }
    },
    [addLog, onPlaybackComplete],
  )

  const handleWebSocketError = useCallback(
    (error: string) => {
      addLog(error, 'error')
      setErrorMsg(error)
      setAppState('error')
    },
    [addLog],
  )

  const handleWebSocketClose = useCallback(() => {
    addLog('WebSocket 连接已关闭')
    if (appState === 'thinking' || appState === 'speaking') {
      setAppState('idle')
      setIsPlaying(false)
    }
  }, [addLog, appState])

  // ---------------------------------------------------------------------------
  // 录音相关函数
  // ---------------------------------------------------------------------------

  const startRecording = async () => {
    try {
      console.log('[Talk-o] 开始录音...')
      addLog('开始录音')

      if (!navigator.mediaDevices || !window.MediaRecorder) {
        throw new Error('浏览器不支持录音')
      }

      // 解锁播放 AudioContext（必须用户交互后）
      await audioPlayerRef.current?.unlock()

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      })
      recordingStreamRef.current = stream

      const mimeType = getSupportedMimeType()
      if (mimeType === null) {
        throw new Error('浏览器不支持录音')
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
      })

      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []
      recordingStartTimeRef.current = Date.now()

      // 新一轮对话：清空上一次的 AI 文本
      setAiText('')

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        console.log('[Talk-o] 录音结束，处理音频...')
        addLog('录音结束')

        stream.getTracks().forEach((track) => track.stop())
        recordingStreamRef.current = null

        const duration = Date.now() - recordingStartTimeRef.current
        if (duration < RECORDING_CONFIG.MIN_DURATION_MS) {
          addLog('录音太短，已放弃', 'warn')
          setAppState('idle')
          return
        }

        const audioBlob = new Blob(audioChunksRef.current, {
          type: mimeType || 'audio/webm',
        })
        console.log(`[Talk-o] 音频大小: ${audioBlob.size} bytes`)
        addLog(`录音完成: ${audioBlob.size} bytes`)

        setAppState('thinking')
        await sendAudioToServer(audioBlob)
      }

      mediaRecorder.start(100)
      setAppState('recording')

      maxDurationTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          addLog('录音超时，自动停止')
          stopRecording()
        }
      }, RECORDING_CONFIG.MAX_DURATION_MS)
    } catch (error: any) {
      console.error('[Talk-o] 录音失败:', error)
      addLog(`录音失败: ${error.message}`, 'error')
      setErrorMsg('无法访问麦克风，请检查权限设置')
      setAppState('error')
    }
  }

  const stopRecording = () => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current)
      maxDurationTimerRef.current = null
    }

    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }

  // ---------------------------------------------------------------------------
  // WebSocket 通信
  // ---------------------------------------------------------------------------

  const sendAudioToServer = async (audioBlob: Blob) => {
    try {
      const wsUrl = getWebSocketUrl(selectedPersona?.id)
      addLog(`连接 WebSocket: ${wsUrl}`)

      wsManagerRef.current = new WebSocketManager(
        wsUrl,
        handleWebSocketMessage,
        handleWebSocketError,
        handleWebSocketClose,
      )

      await wsManagerRef.current.connect()
      addLog('WebSocket 连接成功')

      wsManagerRef.current.sendAudio(audioBlob)
      addLog('音频已发送，等待 AI 响应...')
    } catch (error: any) {
      console.error('[Talk-o] WebSocket 错误:', error)
      addLog(`通信失败: ${error.message}`, 'error')
      setErrorMsg('网络连接失败')
      setAppState('error')
    }
  }

  // ---------------------------------------------------------------------------
  // 权限解锁
  // ---------------------------------------------------------------------------

  const unlockAudioPermissions = async () => {
    try {
      addLog('解锁音频权限...')

      await audioPlayerRef.current?.unlock()
      addLog('AudioContext 已解锁')

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((track) => track.stop())
      addLog('麦克风权限已获取')

      setAppState('idle')
      addLog('准备就绪，可以开始对话')
    } catch (error: any) {
      console.error('[Talk-o] 权限解锁失败:', error)
      addLog(`权限解锁失败: ${error.message}`, 'error')
      setErrorMsg('需要麦克风和音频播放权限')
      setAppState('error')
    }
  }

  // ---------------------------------------------------------------------------
  // UI 事件处理
  // ---------------------------------------------------------------------------

  const handleButtonClick = () => {
    switch (appState) {
      case 'init':
        unlockAudioPermissions()
        break
      case 'idle':
        startRecording()
        break
      case 'recording':
        stopRecording()
        break
      case 'speaking':
        audioPlayerRef.current?.stop()
        wsManagerRef.current?.disconnect()
        setIsPlaying(false)
        setAppState('idle')
        break
      case 'thinking':
        wsManagerRef.current?.disconnect()
        setAppState('idle')
        break
      case 'error':
        setErrorMsg(null)
        setAppState('init')
        unlockAudioPermissions()
        break
    }
  }

  // ---------------------------------------------------------------------------
  // 按钮配置
  // ---------------------------------------------------------------------------

  const getButtonConfig = () => {
    switch (appState) {
      case 'init':
        return {
          gradient: 'from-sky-500 to-indigo-500',
          shadow: 'shadow-sky-300/50',
          text: '点击开始',
          subText: '请先允许麦克风权限',
          Icon: SparkleIcon,
          showPulse: false,
          disabled: false,
        }
      case 'idle':
        return {
          gradient: 'from-emerald-400 to-teal-400',
          shadow: 'shadow-emerald-300/50',
          text: '点击说话',
          subText: '按住说话，松开发送',
          Icon: MicIcon,
          showPulse: false,
          disabled: false,
        }
      case 'recording':
        return {
          gradient: 'from-rose-400 to-red-500',
          shadow: 'shadow-rose-300/50',
          text: '录音中',
          subText: '说完后点击结束',
          Icon: StopIcon,
          showPulse: true,
          disabled: false,
        }
      case 'thinking':
        return {
          gradient: 'from-amber-300 to-orange-400',
          shadow: 'shadow-amber-300/50',
          text: '思考中',
          subText: 'AI 正在处理',
          Icon: ThinkingIcon,
          showPulse: true,
          disabled: true,
        }
      case 'speaking':
        return {
          gradient: isPlaying ? 'from-sky-400 to-blue-500' : 'from-indigo-400 to-violet-500',
          shadow: 'shadow-blue-300/50',
          text: isPlaying ? '播放中' : '准备播放',
          subText: 'AI 正在回答',
          Icon: isPlaying ? ThinkingIcon : SparkleIcon,
          showPulse: isPlaying,
          disabled: false,
        }
      case 'error':
        return {
          gradient: 'from-slate-400 to-gray-500',
          shadow: 'shadow-gray-300/50',
          text: '出错了',
          subText: '点我重新试试',
          Icon: SparkleIcon,
          showPulse: false,
          disabled: false,
        }
    }
  }

  const buttonConfig = getButtonConfig()

  // ---------------------------------------------------------------------------
  // 渲染
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50 flex flex-col items-center justify-center p-4 safe-area-inset-top safe-area-inset-bottom">
      {/* 背景装饰圆球 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-cyan-100/40 blur-3xl" />
        <div className="absolute top-1/3 -right-20 w-80 h-80 rounded-full bg-indigo-200/40 blur-3xl" />
        <div className="absolute -bottom-24 left-1/3 w-96 h-96 rounded-full bg-cyan-100/40 blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <div className="relative z-10 w-full max-w-md mb-4">
        <div className="flex items-center justify-between">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 border border-white/80 text-slate-600 text-xs font-medium hover:bg-white hover:shadow-sm transition-all"
          >
            <span>🏠</span>
            <span>返回首页</span>
          </a>
          <a
            href="/translate"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 border border-white/80 text-slate-600 text-xs font-medium hover:bg-white hover:shadow-sm transition-all"
          >
            <span>🌐</span>
            <span>同声传译</span>
          </a>
        </div>
      </div>

      {/* 主卡片 */}
      <div className="relative z-10 w-full max-w-md">
        <div className="backdrop-blur-xl bg-white/70 rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(124,58,237,0.18)] border border-white/60 p-6 sm:p-8">
          {/* 标题区 */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 shadow-lg shadow-sky-300/40 mb-4">
              <span className="text-3xl" role="img" aria-label="Talk-o">
                🎙️
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-sky-600 via-indigo-600 to-slate-700 bg-clip-text text-transparent tracking-tight">
              Talk-o
            </h1>
            <p className="text-slate-500 text-sm mt-2 font-medium">语音对话 · 实时翻译 · 跨语言沟通</p>

            {/* 角色选择器 */}
            <div className="mt-4">
              {loadingPersonas ? (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 border border-white/80 text-slate-400 text-xs">
                  <span className="w-3 h-3 border-2 border-slate-300 border-t-violet-400 rounded-full animate-spin" />
                  加载角色中...
                </div>
              ) : selectedPersona ? (
                <button
                  onClick={() => setShowPersonaSelector(true)}
                  disabled={appState === 'recording' || appState === 'thinking' || appState === 'speaking'}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 border border-sky-100 shadow-sm text-slate-700 text-sm font-medium hover:bg-white hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-sky-300 to-indigo-300 flex items-center justify-center text-xs">🦄</span>
                  <span>正在和 <span className="font-bold text-sky-600">{selectedPersona.name}</span> 聊天</span>
                  <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 10l5 5 5-5H7z" />
                  </svg>
                </button>
              ) : (
                <div className="text-rose-500 text-xs font-medium">暂无可用角色，请联系管理员创建</div>
              )}
            </div>
          </div>

          {/* 角色选择弹窗 */}
          {showPersonaSelector && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-sm">
              <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-5 border border-white/60">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-800">选择 AI 角色</h3>
                  <button
                    onClick={() => setShowPersonaSelector(false)}
                    className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition-colors"
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                  {personas.map((persona) => (
                    <button
                      key={persona.id}
                      onClick={() => {
                        setSelectedPersona(persona)
                        setShowPersonaSelector(false)
                        addLog(`切换角色: ${persona.name}`)
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ${selectedPersona?.id === persona.id
                        ? 'border-sky-500 bg-sky-50 shadow-sm'
                        : 'border-transparent bg-slate-50 hover:bg-slate-100'
                        }`}
                    >
                      <span className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-300 to-indigo-300 flex items-center justify-center text-lg shrink-0">
                        {persona.is_active ? '⭐' : '🐾'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-800 truncate">{persona.name}</div>
                        <div className="text-xs text-slate-500 truncate">{persona.voice}</div>
                      </div>
                      {selectedPersona?.id === persona.id && (
                        <span className="w-5 h-5 rounded-full bg-violet-500 text-white flex items-center justify-center text-xs">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 错误提示 */}
          {errorMsg && (
            <div className="mb-5 px-4 py-3 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl text-sm text-center font-medium animate-pulse">
              {errorMsg}
            </div>
          )}

          {/* 录音波纹区 */}
          <div
            className={`h-28 sm:h-32 mb-2 transition-all duration-500 ${appState === 'recording' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 h-0 overflow-hidden'
              }`}
          >
            <RecordingWaveform stream={recordingStreamRef.current} isRecording={appState === 'recording'} />
          </div>

          {/* 主按钮 */}
          <div className="flex justify-center my-4">
            <button
              onClick={handleButtonClick}
              disabled={buttonConfig.disabled}
              className={`
                relative w-40 h-40 sm:w-44 sm:h-44 rounded-full
                bg-gradient-to-br ${buttonConfig.gradient}
                text-white shadow-[0_16px_40px_-10px] ${buttonConfig.shadow}
                transition-all duration-200 ease-out
                flex flex-col items-center justify-center
                ${buttonConfig.disabled ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}
              `}
              aria-label={buttonConfig.text}
            >
              {buttonConfig.showPulse && (
                <span className="absolute inset-0 rounded-full animate-ping bg-white/20" />
              )}
              {buttonConfig.showPulse && (
                <span className="absolute -inset-3 rounded-full border-4 border-white/30 animate-pulse-ring" />
              )}

              <buttonConfig.Icon className="w-10 h-10 sm:w-12 sm:h-12 mb-2 drop-shadow-md" />
              <span className="text-lg sm:text-xl font-bold drop-shadow-md">{buttonConfig.text}</span>
              <span className="text-xs opacity-85 mt-1 font-medium">{buttonConfig.subText}</span>
            </button>
          </div>

          {/* AI 文本气泡 */}
          <div
            className={`transition-all duration-500 ${aiText ? 'opacity-100 translate-y-0 max-h-96' : 'opacity-0 translate-y-4 max-h-0 overflow-hidden'
              }`}
          >
            {aiText && (
              <div className="mt-4 p-4 rounded-2xl rounded-tl-sm bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-100 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🤖</span>
                  <span className="text-xs font-bold text-blue-500 uppercase tracking-wider">AI 说</span>
                </div>
                <p className="text-slate-700 text-base leading-relaxed font-medium">{aiText}</p>
              </div>
            )}
          </div>

          {/* 状态说明 */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 border border-white/80 shadow-sm">
              <span
                className={`w-2 h-2 rounded-full ${appState === 'recording'
                  ? 'bg-rose-500 animate-pulse'
                  : appState === 'thinking'
                    ? 'bg-amber-500 animate-pulse'
                    : appState === 'speaking'
                      ? 'bg-sky-500 animate-pulse'
                      : appState === 'error'
                        ? 'bg-gray-500'
                        : 'bg-emerald-500'
                  }`}
              />
              <span className="text-slate-500 text-sm font-medium">
                {appState === 'init' && '准备开始'}
                {appState === 'idle' && '随时可以说话'}
                {appState === 'recording' && '正在听你说话'}
                {appState === 'thinking' && 'AI 正在理解'}
                {appState === 'speaking' && 'AI 正在回答'}
                {appState === 'error' && '请重试'}
              </span>
            </div>
          </div>
        </div>

        {/* 调试面板切换 */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className="text-slate-400 text-xs hover:text-slate-600 font-medium px-3 py-1 rounded-full bg-white/40 backdrop-blur-sm transition-colors"
          >
            {showDebugPanel ? '隐藏调试面板' : '显示调试面板'}
          </button>
        </div>

        {/* 调试面板 */}
        {showDebugPanel && (
          <div className="mt-3 w-full bg-slate-900/90 backdrop-blur rounded-2xl p-4 text-xs font-mono shadow-xl border border-slate-700/50">
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-400 font-bold">调试日志</span>
              <button
                onClick={() => setDebugLogs([])}
                className="text-slate-500 hover:text-slate-300 transition-colors"
              >
                清空
              </button>
            </div>
            <div className="h-40 overflow-y-auto space-y-1 pr-1">
              {debugLogs.length === 0 && <div className="text-slate-600 italic">暂无日志...</div>}
              {debugLogs.map((log) => (
                <div
                  key={log.id}
                  className={`
                    ${log.level === 'error' ? 'text-rose-400' : ''}
                    ${log.level === 'warn' ? 'text-amber-400' : ''}
                    ${log.level === 'info' ? 'text-emerald-400' : ''}
                  `}
                >
                  <span className="text-slate-500">[{log.timestamp}]</span>{' '}
                  {log.message}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

